// CSS import
import './style.css'

// 1. Import the Agents SDK library
import { createAgentManager, StreamType } from '@d-id/client-sdk';

// Import session manager
import {
  initializeFinishButton,
  initializeStartNewButton,
  setSessionId,
  getSessionId
} from './sessionManager.js';

// Backend is proxied through Vite (/api → http://localhost:3001) so we use
// relative paths. This avoids mixed-content blocks when the page is HTTPS.
const BACKEND_URL = '';

// Read auth params passed by Flutter via URL query string (?token=...&childId=...)
const _urlParams = new URLSearchParams(window.location.search);
const childToken = _urlParams.get('token');
const childId = _urlParams.get('childId');

// Store token in sessionStorage so sessionManager can access it
if (childToken) {
  sessionStorage.setItem('childToken', childToken);
}

// 3. Fetch credentials from backend
let auth;
let agentId;

// Variables declaration
let streamVideoElement = document.querySelector("#streamVideoElement")
let idleVideoElement = document.querySelector("#idleVideoElement")
let textArea = document.querySelector("#textArea")
let speechButton = document.querySelector("#speechButton");
let answers = document.querySelector("#answers")
let connectionLabel = document.querySelector("#connectionLabel")
let reconnectButton = document.querySelector("#reconnectButton")
let actionButton = document.querySelector("#actionButton");
let videoWrapper = document.querySelector("#video-wrapper");
let interruptButton = document.querySelector("#interruptButton");
let renderedMessageIds = new Set();
let srcObject;
let streamType;
// Tracks the last value passed to onVideoStateChange so we can ignore
// redundant START/STOP events that D-ID fires per-audio-chunk during a
// single response. Reassigning srcObject on every START causes a decoder
// reset and visible video stutter.
let lastVideoState = null;

// Status toast helpers
const statusToast = document.getElementById('statusToast');
const statusToastMsg = document.getElementById('statusToastMsg');
let _toastDismissTimer = null;

function showToast(message) {
    if (_toastDismissTimer) { clearTimeout(_toastDismissTimer); _toastDismissTimer = null; }
    statusToastMsg.textContent = message;
    statusToast.querySelector('.statusToast__dots').style.display = '';
    statusToast.classList.remove('statusToast--hidden', 'statusToast--dismissing');
}

function dismissToast(delay = 0) {
    _toastDismissTimer = setTimeout(() => {
        statusToast.classList.add('statusToast--dismissing');
    }, delay);
}

// Pending user message (waiting for agent response)
let pendingUserMessage = null;

// Personalization context about the child (fetched from the backend at startup).
// Prepended to chat() prompts so the companion knows who it's talking to. Empty
// for guests or if the fetch fails — the companion still works without it.
let childContextPrompt = '';

// 4. Define the SDK callbacks functions here
const callbacks = {

    // Link the HTML Video element with the WebRTC Stream Object (Video & Audio tracks)
    onSrcObjectReady(value) {
        console.log("SrcObject Ready")
        streamVideoElement.srcObject = value
        srcObject = value
        return srcObject
    },

    // Connection States callback method
    onConnectionStateChange(state) {
        console.log("Connection State: ", state)

        if (state == "connecting") {
            showToast("Connecting");
            connectionLabel.innerHTML = "Connecting.."
            document.querySelector("#container").style.display = "flex"
            document.querySelector("#hidden").style.display = "none"

            // Displaying the Agent's name in the pages' header
            document.querySelector("#previewName").innerHTML = agentManager.agent.preview_name

            // Setting the video elements' sources and display
            idleVideoElement.muted = true;
            idleVideoElement.loop = true;
            idleVideoElement.setAttribute('playsinline', '');
            idleVideoElement.src = agentManager.agent.presenter.idle_video;
            idleVideoElement.load();
            idleVideoElement.play().catch(e => console.warn('[Main] Idle video play failed:', e));
            videoWrapper.style.filter = "blur(5px)"
            streamVideoElement.style.opacity = 0;
            idleVideoElement.style.opacity = 1;

            // Setting video background image to avoid "flickering" for the Legacy streaming architecture:
            videoWrapper.style.backgroundImage = `url(${agentManager.agent.presenter.thumbnail})`
            // (For photo-based avatars, set the following: `url(${agentManager.agent.presenter.source_url})`
            // For Premium+ avatars, set the following: `url(${agentManager.agent.presenter.thumbnail})`
            // Alternativley, save the first frame of the Idle video locally and set it as background image.)
        }

        else if (state == "connected") {
            // Setting the 'Tab' key to switch between the modes and 'Enter' Key to Send a message
            textArea.addEventListener('keypress', (event) => { if (event.key === "Enter") { event.preventDefault(); handleAction() } })
            document.addEventListener('keydown', (event) => { if (event.key === 'Tab') { event.preventDefault(); switchModes() } })
            actionButton.removeAttribute("disabled")
            speechButton.removeAttribute("disabled")
            answers.innerHTML += `<div class='agentMessage'>${agentManager.agent.greetings[0]}</div>`
            if (streamType !== StreamType.Fluent) {
                connectionLabel.innerHTML = "Connected"
                videoWrapper.style.filter = "blur(0px)"
            }
            statusToastMsg.textContent = "Connected";
            statusToast.querySelector('.statusToast__dots').style.display = 'none';
            dismissToast(1500);

            // Warm up the browser-level microphone permission so the first
            // tap on the mic button doesn't get blocked. We acquire the stream
            // then immediately stop all tracks — we only need the permission grant.
            navigator.mediaDevices?.getUserMedia({ audio: true })
                .then(stream => stream.getTracks().forEach(t => t.stop()))
                .catch(err => console.warn('[Main] Mic warm-up failed (non-fatal):', err.message));
        }

        else if (state == "disconnected" || state == "closed") {
            textArea.removeEventListener('keypress', (event) => { if (event.key === "Enter") { event.preventDefault(); handleAction() } })
            document.removeEventListener('keydown', (event) => { if (event.key === 'Tab') { event.preventDefault(); switchModes() } })
            document.querySelector("#hidden_h2").innerHTML = `${agentManager.agent.preview_name} Disconnected`
            document.querySelector("#hidden").style.display = "flex"
            document.querySelector("#container").style.display = "none"
            actionButton.setAttribute("disabled", true)
            speechButton.setAttribute("disabled", true)
            document.getElementById("video-wrapper").style.filter = "blur(5px)"
            connectionLabel.innerHTML = ""
        }
    },

    // Video State callback method (Legacy and Fluent architectures)
    //
    // IMPORTANT: D-ID fires this callback multiple times per response — once per
    // audio/video chunk. A long reply can trigger 5+ START events in quick
    // succession with no intervening STOP. We must treat redundant same-state
    // events as no-ops, or we'll re-run DOM work (especially `srcObject =`) that
    // causes visible video stutter mid-response.
    onVideoStateChange(state) {
        console.log("Video State: ", state, "(prev:", lastVideoState, ")")

        // De-dupe: ignore repeat events for the same state. Skip DOM work entirely.
        if (state === lastVideoState) return;
        const prevState = lastVideoState;
        lastVideoState = state;

        // FLUENT - NEW ARCHITECURE (Fluent: Single Video for both Idle and Streaming)
        if (streamType == StreamType.Fluent) {
            if (state == "START") {
                videoWrapper.style.filter = "blur(0px)"
                connectionLabel.innerHTML = "Connected"
                streamVideoElement.style.opacity = 1;
                idleVideoElement.style.opacity = 0;
            }
        }
        // LEGACY - OLD ARCHITECURE (Legacy: Switching between the idle and streamed videos elements)
        else {
            if (state == "START") {
                streamVideoElement.muted = false
                // Only (re)attach the MediaStream if it isn't already bound.
                // Reassigning srcObject forces a decoder reset even if the stream
                // is the same object, which is exactly what causes mid-response stutter.
                if (streamVideoElement.srcObject !== srcObject) {
                    streamVideoElement.srcObject = srcObject
                }
                idleVideoElement.style.opacity = 0;
                streamVideoElement.style.opacity = 1;
            }
            else {
                streamVideoElement.muted = true
                idleVideoElement.style.opacity = 1;
                idleVideoElement.play().catch(e => console.warn('[Main] Idle resume failed:', e));
                streamVideoElement.style.opacity = 0;
            }
        }
    },

    // New messages callback method
    onNewMessage(messages, type) {
        let lastIndex = messages.length - 1;
        let msg = messages[lastIndex];
        if (!msg) return;

        if (msg && msg.role == "assistant" && messages.length != 1) {
            if (type == "answer") {
                if (msg.id && renderedMessageIds.has(msg.id)) return;
                if (msg.id) renderedMessageIds.add(msg.id);

                answers.innerHTML += `<div class='agentMessage'>${msg.content}</div>`;
                console.log(`New Message:\n[${msg.role}] ${msg.content}`);
                connectionLabel.innerHTML = "Online";

                // Send turn to backend with both user message and agent response
                if (pendingUserMessage) {
                    sendTurnToBackend(pendingUserMessage, null, msg.content);
                    pendingUserMessage = null;
                }
            }
        }

        answers.scrollTo({
            top: answers.scrollHeight + 50,
            behavior: 'smooth'
        });
    },


    // Agent's Talking/Idle states for INTERRUPT (only valid for the new Fluent architecutre)
    onAgentActivityStateChange(state) {
        console.log("Agent Activity State: ", state)
        if (state == "TALKING") {
            interruptButton.style.display = "inline-flex"
            speechButton.style.display = "none"
            actionButton.style.display = "none"
        }
        else {
            interruptButton.style.display = "none"
            speechButton.style.display = "inline-flex"
            actionButton.style.display = "inline-flex"
        }
    },

    // Error handling
    onError(error, errorData) {
        connectionLabel.innerHTML = `<span style="color:red">Something went wrong :(</span>`
        console.log("Error:", error, "Error Data:", errorData)
    }

}

// 5. Define the Stream options object
// NOTE: must match the original D-ID demo — `fluent: true` selects the Fluent
// streaming architecture (required for INTERRUPT + the Fluent idle/stream video
// handling in onVideoStateChange). `compatibilityMode: "on"` is what the demo
// shipped with and is known to work; "auto" has caused connection issues.
let streamOptions = { compatibilityMode: "on", streamWarmup: true, fluent: true }

// - - - - - - - - - - - - Local functions to utilize the Agent's SDK methods: - - - - - - - - - - - - - //

// agentManager.speak() -> Streams the provided text/audio
function speak() {
    if (!agentManager) { console.warn('[Main] speak() called but agent not connected'); return; }
    let val = textArea.value
    // 'Speak' supports a minimum of 3 characters
    if (val !== "" && val.length > 2) {
        let speak = agentManager.speak(
            {
                type: "text",
                input: val,
            }
        )
        console.log(`Speak: "${val}"`)

        // Store user message, will be sent when agent responds
        pendingUserMessage = val;

        textArea.value = ""
    }
}

// agentManager.chat() -> Streams the D-ID's LLM and Knowledge Response
function chat() {
    if (!agentManager) { console.warn('[Main] chat() called but agent not connected'); return; }
    let val = textArea.value
    if (val !== "") {
        // Render user bubble immediately with the original text (before wrapping with prompt)
        answers.innerHTML += `<div class="userMessage">${val}</div>`;
        answers.scrollTo({ top: answers.scrollHeight, behavior: 'smooth' });

        // Build the prompt: personalization context (if any) + language instruction
        // + the user's message. The user only ever sees `val` (rendered above);
        // the context/instructions are invisible steering for the LLM.
        const contextBlock = childContextPrompt
            ? `[About the child you are talking to]\n${childContextPrompt}\n\n`
            : '';
        const prompt = `${contextBlock}Detect the language of the user's message and respond ONLY in that same language. If Arabic, respond in Arabic. If English, respond in English.\n\nUser: ${val}`;
        agentManager.chat(prompt)
        console.log(`Chat: ("${val}")`)
        connectionLabel.innerHTML = "Thinking.."

        // Store user message, will be sent when agent responds
        pendingUserMessage = val;

        textArea.value = ""
    }
}

/**
 * Send a conversation turn to the backend
 * @param {string} userText - The user's message
 * @param {Blob} audioBlob - Optional audio recording
 * @param {string} agentResponse - The agent's response
 */
async function sendTurnToBackend(userText, audioBlob = null, agentResponse = null) {
    const sessionId = getSessionId();
    if (!sessionId) {
        console.warn('[Main] No session ID, skipping turn tracking');
        return;
    }

    try {
        const formData = new FormData();
        formData.append('text', userText);

        if (audioBlob) {
            formData.append('audio', audioBlob, 'recording.webm');
        }

        if (agentResponse) {
            formData.append('agentResponse', agentResponse);
        }

        const token = sessionStorage.getItem('childToken');
        const turnHeaders = {};
        if (token) turnHeaders['Authorization'] = `Bearer ${token}`;

        const response = await fetch(`${BACKEND_URL}/api/companion/session/${sessionId}/turn`, {
            method: 'POST',
            headers: turnHeaders,
            body: formData
        });

        if (response.ok) {
            const data = await response.json();
            console.log(`[Main] Turn ${data.turnNumber} saved to backend (with agent response)`);
        } else {
            console.warn('[Main] Failed to save turn to backend:', response.status);
        }
    } catch (error) {
        console.error('[Main] Error sending turn to backend:', error);
    }
}

// agentManager.interrupt() -> Interrupts the Agent's response - Only supported for Fluent architecture + Premium+ Avatars.
function interrupt() {
    if (!agentManager) { console.warn('[Main] interrupt() called but agent not connected'); return; }
    let interrupt = agentManager.interrupt({ type: "click" })
    console.log("Interrupt")
}

// agentManager.rate() -> Rating the Agent's answers - for future Agents Analytics and Insights feature
function rate(messageID, score) {
    if (!agentManager) { console.warn('[Main] rate() called but agent not connected'); return; }
    let rate = agentManager.rate(messageID, score)
    console.log(`Message ID: ${messageID} Rated:${score}\n`, "Result", rate)
}

// agentManager.reconnect() -> Reconnect the Agent to a new WebRTC session
// If the agent was never connected (initial connect failed), re-run initializeAgent
// instead of calling .reconnect() on a null reference.
function reconnect() {
    if (!agentManager) {
        console.log('[Main] reconnect: agent was never connected, re-initializing');
        initializeAgent();
        return;
    }
    let reconnect = agentManager.reconnect()
    console.log("Reconnect")
}

// agentManager.disconnect() -> Terminates the current Agent's WebRTC session
function disconnect() {
    if (!agentManager) { return; }
    let disconnect = agentManager.disconnect()
    console.log("Disconnect")
}

// - - - - - - - - - - - - Utility Functions - - - - - - - - - - - - //

// Fetch configuration from backend
async function fetchConfig() {
    try {
        const response = await fetch(`${BACKEND_URL}/api/companion/config`);        
        if (!response.ok) {
            throw new Error(`Backend returned ${response.status}: ${response.statusText}`);
        }
        const config = await response.json();
        return config;
    } catch (error) {
        console.error('Failed to fetch config from backend:', error);
        connectionLabel.innerHTML = `<span style='color:red; font-weight:bold'>Failed to connect to backend</span>`;
        throw error;
    }
}

// Fetch the child's personalization context (name, interests, mood, etc.).
// Best-effort: never blocks or breaks the companion if it fails.
async function fetchChildContext() {
    if (!childToken) return; // guest — no personalization
    try {
        const response = await fetch(`${BACKEND_URL}/api/companion/context`, {
            headers: { 'Authorization': `Bearer ${childToken}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data.hasContext && data.contextPrompt) {
            childContextPrompt = data.contextPrompt;
            console.log('[Main] Child context loaded:', childContextPrompt);
        }
    } catch (error) {
        console.warn('[Main] Failed to fetch child context (continuing without):', error);
    }
}

// Connectivity diagnostic
async function testConnectivity() {
    try {
        const res = await fetch('https://agent.d-id.com/health', { method: 'HEAD', mode: 'no-cors' });
        console.log('[Main] D-ID connectivity: OK');
    } catch (err) {
        console.warn('[Main] D-ID connectivity test failed:', err.message);
        try {
            await fetch('https://www.google.com', { method: 'HEAD', mode: 'no-cors' });
            console.log('[Main] General internet: OK — D-ID-specific issue');
        } catch (e) {
            console.error('[Main] No internet access from WebView:', e.message);
        }
    }
}

// NOTE: credential fetch used to run at module-top-level with `await`, which
// blocked the entire script (and therefore the UI) on a round-trip to the
// backend. Now we do it inside initializeApp() so the page renders immediately
// and we can show a loading state while credentials load.

// Chat/Speak Selection Logic
function switchModes() {
    const options = document.querySelectorAll('#buttons input[name="option"]');
    const checkedIndex = Array.from(options).findIndex(opt => opt.checked);

    const nextIndex = (checkedIndex + 1) % options.length;
    options[nextIndex].checked = true;
}
function handleAction() {
    const selectedOption = document.querySelector('input[name="option"]:checked').value;
    if (selectedOption === 'chat') {
        chat();
    } else if (selectedOption === 'speak') {
        speak();
    }
}

// ---- Language toggle ----
// Initialise from URL param (?lang=ar) so Flutter can pre-select Arabic.
const _urlLang = _urlParams.get('lang');
let _currentLang = (_urlLang === 'ar') ? 'ar-SA' : 'en-US';

const _langToggle = document.getElementById('languageToggle');
if (_langToggle) {
    // Sync initial active pill with the resolved language
    _langToggle.querySelectorAll('.langOption').forEach(el => {
        el.classList.toggle('active', el.dataset.lang === (_currentLang === 'ar-SA' ? 'ar' : 'en'));
    });
    _langToggle.addEventListener('click', (e) => {
        const pill = e.target.closest('.langOption');
        if (!pill) return;
        _langToggle.querySelectorAll('.langOption').forEach(el => el.classList.remove('active'));
        pill.classList.add('active');
        _currentLang = pill.dataset.lang === 'ar' ? 'ar-SA' : 'en-US';
        console.log('[Main] Recognition language →', _currentLang);
    });
}

// ---- Web Speech API (mic button) ----
let _recognition = null;
let _isListening = false;

function toggleStartStop() {
    if (_isListening) {
        _stopListening();
    } else {
        _startListening();
    }
}

function _startListening() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
        showToast('Speech recognition is not supported in this browser');
        return;
    }

    _recognition = new SpeechRecognition();
    _recognition.continuous = false;
    _recognition.interimResults = true;
    _recognition.lang = _currentLang;

    _recognition.onstart = () => {
        _isListening = true;
        speechButton.classList.add('recording');
        textArea.placeholder = 'Listening…';
    };

    _recognition.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
            transcript += event.results[i][0].transcript;
        }
        textArea.value = transcript;
    };

    _recognition.onend = () => {
        _isListening = false;
        _recognition = null;
        speechButton.classList.remove('recording');
        textArea.placeholder = 'Tell me something fun! ';
        // Auto-send if something was captured
        if (textArea.value.trim().length > 0) {
            handleAction();
        }
    };

    _recognition.onerror = (event) => {
        console.error('[Speech] Error:', event.error);
        _isListening = false;
        _recognition = null;
        speechButton.classList.remove('recording');
        textArea.placeholder = 'Tell me something fun! ';
        if (event.error === 'not-allowed') {
            showToast('Microphone access denied — allow mic in browser/site settings');
        } else if (event.error === 'no-speech') {
            showToast('No speech detected — try again');
            dismissToast(2000);
        } else {
            showToast(`Mic error: ${event.error}`);
            dismissToast(2500);
        }
    };

    try {
        _recognition.start();
    } catch (e) {
        console.error('[Speech] Failed to start recognition:', e);
        _isListening = false;
        _recognition = null;
    }
}

function _stopListening() {
    if (_recognition) {
        _recognition.stop();
        // onend fires automatically and cleans up state
    }
}

// Event Listeners for Agent's built-in methods
actionButton.addEventListener('click', handleAction);
speechButton.addEventListener('click', () => toggleStartStop())
reconnectButton.addEventListener('click', () => reconnect())
interruptButton.addEventListener('click', () => interrupt())

// Agent manager
let agentManager = null;

// Initialize the app
async function initializeApp() {
    console.log('[Main] Initializing app...');

    textArea.focus()
    actionButton.setAttribute("disabled", true)
    speechButton.setAttribute("disabled", true)

    // Initialize session manager UI (synchronous, no network)
    initializeFinishButton();
    initializeStartNewButton();

    // Fetch credentials. This is the first network call the user waits on —
    // show the toast so they know something is happening.
    showToast('Loading…');
    try {
        const config = await fetchConfig();
        auth = { type: 'key', clientKey: config.clientKey };
        agentId = config.agentId;
        console.log('[Main] Auth + agentId loaded');
    } catch (error) {
        console.error('[Main] Configuration error:', error);
        statusToastMsg.textContent = 'Cannot reach backend — is the server running?';
        statusToast.querySelector('.statusToast__dots').style.display = 'none';
        statusToast.classList.remove('statusToast--hidden', 'statusToast--dismissing');
        return;
    }

    // Run backend session, D-ID agent init, and child-context fetch in parallel
    // now that we have creds. Context fetch is best-effort and won't block.
    await Promise.all([startBackendSession(), initializeAgent(), fetchChildContext()]);
}

// Run initialization immediately (module scripts are deferred, so DOM is ready)
initializeApp();

// - - - - - - - - - - - - Cleanup: disconnect stream on page close - - - - - - - - - - - - //
// CRITICAL: without this, every refresh / tab close / WebView kill leaves the
// D-ID stream open on their servers, which accumulates and produces
// "Max user sessions reached" 403 errors on subsequent connects.
//
// `beforeunload` fires on desktop tab close / refresh.
// `pagehide` fires on iOS Safari / WKWebView (beforeunload is unreliable there).
// We intentionally DO NOT hook `visibilitychange` because brief backgrounding
// (user reads a text message) should not kill the session.
function teardownAgent(reason) {
    if (!agentManager) return;
    console.log('[Main] Tearing down agent:', reason);
    try {
        agentManager.disconnect();
    } catch (e) {
        console.warn('[Main] disconnect threw during teardown:', e);
    }
    agentManager = null;
}

window.addEventListener('beforeunload', () => teardownAgent('beforeunload'));
window.addEventListener('pagehide', () => teardownAgent('pagehide'));

// Listen for session ending event
document.addEventListener('sessionEnding', () => {
    console.log('[Main] Session ending event received');
    if (agentManager) {
        console.log('[Main] Disconnecting agent manager...');
        agentManager.disconnect();
        agentManager = null; // Clear the reference
        console.log('[Main] Agent manager disconnected and cleared');
    }
    // Clear rendered messages
    renderedMessageIds.clear();
});

/**
 * Start a session in the backend
 */
async function startBackendSession() {
    try {
        const sessionHeaders = { 'Content-Type': 'application/json' };
        if (childToken) sessionHeaders['Authorization'] = `Bearer ${childToken}`;

        const response = await fetch(`${BACKEND_URL}/api/companion/session/start`, {
            method: 'POST',
            headers: sessionHeaders,
            body: JSON.stringify({ mode: 'chat', childId })
        });

        if (!response.ok) {
            console.warn('Backend session creation failed, continuing without session tracking');
            return;
        }

        const data = await response.json();
        setSessionId(data.sessionId);
        console.log('Backend session started:', data.sessionId);
    } catch (error) {
        console.error('Failed to start backend session:', error);
        // Continue without backend session for now
    }
}

/**
 * Initialize the D-ID agent
 */
async function initializeAgent() {
    console.log('[Main] initializeAgent called');
    console.log('[Main] Current state:', {
        hasAgentManager: !!agentManager,
        hasAuth: !!auth,
        hasAgentId: !!agentId
    });

    if (agentManager) {
        console.log('[Main] Agent already initialized, skipping');
        return;
    }

    // Check if we have valid credentials
    if (!auth || !agentId) {
        console.error('[Main] Missing auth or agentId - cannot initialize agent');
        connectionLabel.innerHTML = `<span style='color:red'>Configuration error - please refresh</span>`;
        return;
    }

    // Single attempt — do NOT retry on failure.
    // Reason: when the failure is "Max user sessions reached" (D-ID 403), each
    // retry opens another stream attempt and burns more of our concurrent-session
    // quota, pushing us deeper into the hole. The SDK already retries internally
    // a few times before surfacing the error. If we still fail, surface a clear
    // message and let the user tap the reconnect button manually.
    try {
        connectionLabel.innerHTML = 'Connecting...';
        agentManager = await createAgentManager(agentId, { auth, callbacks, streamOptions });
        await agentManager.connect();
        streamType = agentManager.getStreamType();
        console.log('[Main] Agent connected. streamType =', streamType,
            '(Fluent =', StreamType.Fluent, ')');
    } catch (error) {
        const msg = String(error?.message || error || '');
        console.warn('[Main] Agent connect failed:', msg);
        agentManager = null;
        if (msg.includes('Max user sessions')) {
            statusToastMsg.textContent = 'D-ID quota reached — wait ~10 min or regenerate key';
        } else {
            statusToastMsg.textContent = 'Could not connect — tap the refresh button to retry';
        }
        statusToast.querySelector('.statusToast__dots').style.display = 'none';
        statusToast.classList.remove('statusToast--hidden', 'statusToast--dismissing');
    }
}

// Happy Coding! 
